import React, { useContext, useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material//Clear';
import { Box, Chip } from '@mui/material';
import { isEmpty } from 'lodash';
import { GPCComp, PTComp } from 'api';
import PerfectScrollbar from 'components/SafePerfectScrollbar';
import CompGroupDialogContext from 'pages/Valuations/components/CompGroups/components/common/CompGroupDialogContext';
import FormLabelCompGroup from 'pages/Valuations/components/CompGroups/components/common/FormLabelCompGroup';
import theme from 'theme';
import { CompGroupCompany, CompGroupSelected } from '../common/types';

const Companies = () => {
  const {
    currentCompGroups,
    currentComparisons,
    compGroupSelected,
    setCompGroupSelected,
    setCompaniesToDelete,
    setCompGroupToDelete,
    setValid,
    isGPC,
  } = useContext(CompGroupDialogContext);
  const [allowedCompanies, setAllowedCompanies] = useState<CompGroupCompany[]>([]);

  const findPTMatch = (currentItem: PTComp, newItem: PTComp) =>
    currentItem.target_name === newItem.target_name && currentItem.acquirer_name === newItem.acquirer_name;

  const gpcFiltered = (gpcData: GPCComp[], comp: GPCComp, matchDistinct = true) =>
    gpcData.filter(item => {
      if (matchDistinct) {
        return item.cap_iq_id !== comp.cap_iq_id;
      }
      return item.cap_iq_id === comp.cap_iq_id;
    });
  const ptFiltered = (ptData: PTComp[], comp: PTComp, matchDistinct = true) =>
    ptData.filter(current => {
      if (matchDistinct) {
        return !findPTMatch(current, comp);
      }
      return findPTMatch(current, comp);
    });

  const setCompaniesToBeRemoved = (comp: CompGroupCompany) => {
    const comparisonsData = isGPC
      ? (gpcFiltered(currentComparisons, comp, false) as CompGroupCompany[])
      : (ptFiltered(currentComparisons, comp, false) as CompGroupCompany[]);
    const matchCompanyKeys = comparisonsData.map(item => item.id || item.temp_id) as number[];
    setCompaniesToDelete(currentState => [...new Set([...currentState, ...matchCompanyKeys])]);
  };

  const setCompGroupsToBeRemoved = () => {
    const matchCompGroup = currentCompGroups.find(compGroupItem => compGroupItem.comp_group === compGroupSelected.id);
    if (matchCompGroup) {
      setCompGroupToDelete(matchCompGroup.comp_group);
    }
  };

  const updateCompSelected = (comp: GPCComp | PTComp) => {
    const currentComps = compGroupSelected.companies;
    const currentCompGroupInfo = {
      ...compGroupSelected,
      companies: isGPC ? gpcFiltered(currentComps, comp) : ptFiltered(currentComps, comp),
      use_latest_comp_group_version: false,
    };

    setCompGroupSelected(currentCompGroupInfo as CompGroupSelected);
  };

  const handleDelete = async (comp: CompGroupCompany) => {
    const newAllowedCompanies = allowedCompanies.filter(item => item.id !== comp.id);
    setCompaniesToBeRemoved(comp);
    setCompGroupsToBeRemoved();
    setAllowedCompanies(newAllowedCompanies);
    updateCompSelected(comp);
    setValid(!isEmpty(newAllowedCompanies));
  };

  useEffect(() => {
    if (compGroupSelected.companies) {
      setAllowedCompanies(Array.isArray(compGroupSelected.companies) ? compGroupSelected.companies : []);
    } else {
      setAllowedCompanies([]);
    }
  }, [compGroupSelected.companies, currentComparisons]);

  const getName = (comp: CompGroupCompany) => {
    if (isGPC) {
      return comp.company_name;
    }
    return `${comp.acquirer_name} | ${comp.target_name}`;
  };

  return (
    <div>
      <FormLabelCompGroup title={isGPC ? 'Companies' : 'Transaction'} />
      <Box width="100%" sx={{ borderRadius: 4, border: `1px solid ${theme.palette.grey[300]}`, minHeight: '8rem' }}>
        <div style={{ padding: '1rem', overflowX: 'auto' }}>
          <PerfectScrollbar>
            {allowedCompanies?.map(comp => (
              <Chip
                key={comp.id}
                color="primary"
                variant="outlined"
                label={getName(comp)}
                size="small"
                deleteIcon={<ClearIcon />}
                onDelete={() => handleDelete(comp)}
                style={{ marginRight: '1rem', marginBottom: '1rem' }}
              />
            ))}
          </PerfectScrollbar>
        </div>
      </Box>
    </div>
  );
};

export default Companies;
